import type { QueryClient } from '@tanstack/react-query';

/**
 * Drop every cached query when the active organization changes.
 *
 * Background (BLU-403): an earlier fix here called
 * `queryClient.invalidateQueries()`, which only marks queries stale and
 * triggers refetches on active observers. React Query keeps the prior
 * resolved data addressable, so any cross-org-sensitive hook whose
 * queryKey does not include `organizationId` (the canonical example is
 * `useConnectedClients` keyed on `locationId` alone) flashes the prior
 * profile's data until the refetch lands. `ClientFilterDropdown` on
 * `/inventory` reproduced this consistently when toggling Client ↔ 3PL.
 *
 * Removing the queries physically empties the slots, so dependent
 * components fall back to their loading state rather than the prior
 * org's snapshot. Mutation cache is intentionally left alone — the
 * tRPC link reads `x-org-id` from localStorage at request-send time
 * (apps/frontend/src/lib/trpc-provider.tsx), and OrgSwitcher writes
 * the new orgId to localStorage before dispatching `org-changed`, so
 * any mutation flushed after the switch already carries the new
 * header. The narrow race where a mutation was `.mutate()`d under
 * the prior org but reaches the wire after the switch is pre-existing
 * and bounded by server-side membership auth.
 */
export function resetCacheOnOrgChange(queryClient: QueryClient): void {
  try {
    queryClient.cancelQueries();
    queryClient.removeQueries();
  } catch {
    // Swallow — cache reset is best-effort; a failure here must not
    // block the org-change UI flow.
  }
}
